import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';

import {
  useCompanyScope,
  useUpdateCompanyScope,
} from '@/features/settings/work-packages/useWorkPackages';

interface CompanyWorkPackageScopePanelProps {
  companyId: string;
}

/**
 * Platform-only panel to enable/disable work packages for one company. Shown on
 * the company edit surface. Reads the current scope, lets an operator toggle
 * each package, and PUTs the full set on save.
 */
export const CompanyWorkPackageScopePanel = ({ companyId }: CompanyWorkPackageScopePanelProps) => {
  const { t } = useTranslation('settings');
  const scopeQuery = useCompanyScope(companyId);
  const updateScope = useUpdateCompanyScope();
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const serverState = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    (scopeQuery.data ?? []).forEach((item) => {
      map[item.work_package_id] = item.enabled;
    });
    return map;
  }, [scopeQuery.data]);

  useEffect(() => {
    setDraft(serverState);
  }, [serverState]);

  const items = scopeQuery.data ?? [];
  const dirty = items.some((item) => (draft[item.work_package_id] ?? false) !== item.enabled);

  const toggle = (workPackageId: string, enabled: boolean) => {
    setDraft((current) => ({ ...current, [workPackageId]: enabled }));
  };

  const save = async () => {
    await updateScope.mutateAsync({
      companyId,
      items: items.map((item) => ({
        work_package_id: item.work_package_id,
        enabled: draft[item.work_package_id] ?? false,
      })),
    });
  };

  return (
    <section className="space-y-3 rounded-md border border-efs-border bg-efs-bg-subtle px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-efs-text-primary">
            {t('efs.settings.work_packages.scope.title')}
          </h3>
          <p className="mt-1 text-xs text-efs-text-muted">
            {t('efs.settings.work_packages.scope.hint')}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => void save()}
          disabled={!dirty || updateScope.isPending}
        >
          {updateScope.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {t('efs.settings.work_packages.scope.save')}
        </Button>
      </div>

      {scopeQuery.isLoading ? (
        <p className="text-xs text-efs-text-muted">
          {t('efs.common.messages.loading', { ns: 'common' })}
        </p>
      ) : items.length === 0 ? (
        <p className="text-xs text-efs-text-muted">{t('efs.settings.work_packages.scope.empty')}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((item) => (
            <li key={item.work_package_id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-efs-bg-panel">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={draft[item.work_package_id] ?? false}
                  onChange={(event) => toggle(item.work_package_id, event.target.checked)}
                />
                <span className="break-all text-efs-text-primary">{item.package_code}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {updateScope.error ? (
        <p className="text-xs text-destructive">{updateScope.error.message}</p>
      ) : null}
    </section>
  );
};
